'use client'

import React, { useEffect, useState } from "react";
import AlertTooltip from "@/components/alert-tooltip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils";
import { FormikProps } from 'formik';
import { InfoIcon, RotateCcw } from 'lucide-react'
import { packagingData } from "@/lib/utils";
// import secondaryImage from '../../../pallet-config/secondary-pallet.jpg'
// import tertiaryImage from '../../../pallet-config/tertiary-pallet.jpg'
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  DATA_COLLECTION_ITEMS_IN_CASE_OPTIONS,
  TERTIARY_PACKAGE_TYPE_OPTIONS,
} from "@/lib/packageOptions";



interface SecondTertiaryPakagingDetailsProps {
  formik: FormikProps<any>;
  setHasUnsavedChanges: any;
}



const SecondTertiaryPakagingDetails: React.FC<SecondTertiaryPakagingDetailsProps> = ({ formik, setHasUnsavedChanges }: { formik: any, setHasUnsavedChanges: any }) => {
  // Helper function to safely convert string to number and validate
  const safeNumberConvert = (value: any): number => {
    if (value === undefined || value === null || value === '') return 0;
    const num = typeof value === 'string' ? Number(value) : value;
    return isNaN(num) ? 0 : num;
  };

  // Helper function to safely format number
  const safeToFixed = (value: any, decimals: number = 2): string => {
    const num = safeNumberConvert(value);
    return num.toFixed(decimals);
  };

  const [isSecondaryNetWeightAutoCalculated, setIsSecondaryNetWeightAutoCalculated] = useState(undefined);
  const [isSecondaryGrossWeightAutoCalculated, setIsSecondaryGrossWeightAutoCalculated] = useState(true);
  const [isSecondaryCaseLengthAutoCalculated, setIsSecondaryCaseLengthAutoCalculated] = useState(true);
  const [isSecondaryCaseWidthAutoCalculated, setIsSecondaryCaseWidthAutoCalculated] = useState(true);
  const [isSecondaryCaseHeightAutoCalculated, setIsSecondaryCaseHeightAutoCalculated] = useState(true);
  const [isTertiaryNetWeightAutoCalculated, setIsTertiaryNetWeightAutoCalculated] = useState(true);
  const [isTertiaryGrossWeightAutoCalculated, setIsTertiaryGrossWeightAutoCalculated] = useState(true);
  const [isTertiaryCaseLengthAutoCalculated, setIsTertiaryCaseLengthAutoCalculated] = useState(true);
  const [isTertiaryCaseWidthAutoCalculated, setIsTertiaryCaseWidthAutoCalculated] = useState(true);
  const [isTertiaryCaseHeightAutoCalculated, setIsTertiaryCaseHeightAutoCalculated] = useState(true);
  const [isSecondaryImageOpen, setIsSecondaryImageOpen] = useState(false);
  const [isTertiaryImageOpen, setIsTertiaryImageOpen] = useState(false);
  // useEffect(() => {
  //   if(isSecondaryNetWeightAutoCalculated === undefined){
  //     setIsSecondaryNetWeightAutoCalculated(formik.values.secondaryPackagingDetails.isUnitNetWeightAutoCalculated);
  //   }
  // }, [formik.values.secondaryPackagingDetails.isUnitNetWeightAutoCalculated]);
  // useEffect(() => {
  //   formik.setFieldValue('secondaryPackagingDetails.isUnitNetWeightAutoCalculated', isSecondaryNetWeightAutoCalculated);
  // }, [isSecondaryNetWeightAutoCalculated]);
  // console.log(isSecondaryNetWeightAutoCalculated,'isSecondaryNetWeightAutoCalculated')
  const secondaryPkg = formik.values.secondaryPackagingDetails.secondaryPkg;
  const tertiaryPkg = formik.values.tertiaryPackagingDetails.tertiaryPkg;
  
  const secondaryPackageTypeOptions = [
      "Base", "Case", "case/pack", "CASE/Shell", "Corrugated Case", "HICONE W Tray", "PAD", "Paperboard","Paperboard Case", "Paperboard Wrap", "RSW", "RSW +Pad", "Shell", "Shrink", "Shrink Tray", "Shrink W Pad", "Shrink W Tray", "Tray", "TRAY & CASE", "Tray -Shrink", "Wrap"
  ];
  const gaugeOptions = secondaryPackageTypeOptions.filter((item)=>item.toLowerCase().includes('tray') || item.toLowerCase().includes('shrink') || item.toLowerCase().includes('paperboard'))

  const tertiaryPackageTypeOptions = TERTIARY_PACKAGE_TYPE_OPTIONS;
  const tertiaryGaugeOptions = tertiaryPackageTypeOptions.filter((item)=>item.toLowerCase().includes('tray') || item.toLowerCase().includes('shrink') || item.toLowerCase().includes('paperboard'))
  const itemsInCase = DATA_COLLECTION_ITEMS_IN_CASE_OPTIONS.map((value, id) => ({ id, value }));
  
  const packageTypeFactor = {
    "Shell": { length: 1.6, width: 0.94, height: 0.62 },
    "Case": { length: 0.49, width: 0.52, height: 0.36 },
    "Corrugated": { length: 0.49, width: 0.52, height: 0.36 },
    "Corrugated Case": { length: 0.49, width: 0.52, height: 0.36 },
    "Paperboard Wrap": { length: 0.25, width: 0.09, height: 0.05 },
    "Paperboard": { length: 0.25, width: 0.09, height: 0.05 },
    "Shrink": { length: -0.28, width: -0.17, height: 0.29 },
    "Shrink Bundle": { length: -0.28, width: -0.17, height: 0.29 },
    "Shrink W Pad": { length: -0.31, width: -0.2, height: 0.87 },
    "Shrink W Tray": { length: -0.05, width: 0.06, height: 0.61 },
    "Shrink Tray": { length: -0.05, width: 0.06, height: 0.61 },
    "Tray -Shrink": { length: -0.05, width: 0.06, height: 0.61 },
    "TRAY & CASE": { length: 0.28, width: 0.17, height: 0.5 },
    "Tray": { length: 0.1, width: 0.1, height: 0.54 },
    "Wrap": { length: -2.2, width: 1.5, height: 0.3 },
    "PAD": { length: -0.35, width: -0.28, height: 0.66 },
    "case/pack": { length: 0.29, width: 0.15, height: 0.22 },
    "Base": { length: 0.8, width: 0.8, height: 0.3 },
    "Others": { length: 0.8, width: 0.8, height: 0.3 },
    "CASE/Shell": { length: 1.6, width: 0.9, height: 0.3 }
  };

  const convertWeight = (value: number, fromUnit: string, toUnit: string): number => {
    // First convert to grams as base unit
    let valueInGrams = value;
    switch (fromUnit) {
      case 'lbs':
        valueInGrams = value * 453.592;
        break;
      case 'oz':
        valueInGrams = value * 28.3495;
        break;
      case 'kg':
        valueInGrams = value * 1000;
        break;
      case 'g':
        valueInGrams = value;
        break;
    }

    // Then convert from grams to target unit
    switch (toUnit) {
      case 'lbs':
        return valueInGrams / 453.592;
      case 'oz':
        return valueInGrams / 28.3495;
      case 'kg':
        return valueInGrams / 1000;
      case 'g':
        return valueInGrams;
      default:
        return value;
    }
  };

  const convertLength = (value: number, fromUnit: string, toUnit: string): number => {
    // Convert to inches first (1 mm = 0.0393701 inches)
    const valueInInches = fromUnit === 'mm' ? value * 0.0393701 : value;

    // Then convert to target unit
    return toUnit === 'mm' ? valueInInches / 0.0393701 : valueInInches;
  };

  const renderUnitSelect = (field: string, options: string[]) => {
    // Determine if this is a tertiary field based on the field name prefix
    const isTertiaryField = field.toLowerCase().startsWith('tertiary');
    const formikPath = isTertiaryField ? 'tertiaryPackagingDetails' : 'secondaryPackagingDetails';

    return (
      <Select
        name={`${formikPath}.${field}Unit`}
        defaultValue={options[0]}
        value={formik.values[formikPath][`${field}Unit`]}
        onValueChange={(value) => {
          const currentValue = formik.values[formikPath][field];
          const currentUnit = formik.values[formikPath][`${field}Unit`];

          // Skip conversion if no current value or unit
          if (!currentValue || !currentUnit) {
            formik.setFieldValue(`${formikPath}.${field}Unit`, value);
            return;
          }

          // Determine if this is a weight or length field
          const isWeightField = field.toLowerCase().includes('weight');
          const convertedValue = isWeightField
            ? convertWeight(Number(currentValue), currentUnit, value)
            : convertLength(Number(currentValue), currentUnit, value);

          formik.setFieldValue(`${formikPath}.${field}`, convertedValue.toFixed(2));
          formik.setFieldValue(`${formikPath}.${field}Unit`, value);
        }}
      >
        <SelectTrigger className="w-[70px] h-6 text-xs bg-unitSelect rounded-[11px] py-3 text-white">
          <SelectValue placeholder="Unit" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={option}>{option}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  const calculateSecondaryPackageNetWeight = () => {
    const netContent = safeNumberConvert(formik.values.primaryPackagingDetails.netContent);
    const netContentUnit = formik.values.primaryPackagingDetails.netContentUnit;
    const secondaryPackageCase = formik.values.secondaryPackagingDetails.secondaryPackageCase;

    if (!netContent || !netContentUnit || !secondaryPackageCase) return 0;

    const netContentOz = netContentUnit === 'ml' ? netContent / 29.5735 : netContent;
    const itemsPerCase = safeNumberConvert(secondaryPackageCase.split(' ')[0]);
    return netContentOz * 0.0625 * itemsPerCase;
  };
  const calculateSecondaryPackageGrossWeight = () => {
    const filledWeight = safeNumberConvert(formik.values.primaryPackagingDetails.filledWeight);
    const filledWeightUnit = formik.values.primaryPackagingDetails.filledWeightUnit;
    const secondaryPackageCase = formik.values.secondaryPackagingDetails.secondaryPackageCase;

    if (!filledWeight || !filledWeightUnit || !secondaryPackageCase) return 0;

    // Convert filled weight to pounds based on unit
    let filledWeightInPounds = filledWeight;
    switch (filledWeightUnit) {
      case 'oz':
        filledWeightInPounds = filledWeight / 16; // 16 oz = 1 lb
        break;
      case 'g':
        filledWeightInPounds = filledWeight / 453.592; // 453.592 g = 1 lb
        break;
      case 'kg':
        filledWeightInPounds = filledWeight * 2.20462; // 1 kg = 2.20462 lbs
        break;
      case 'lbs':
      default:
        break;
    }

    const itemsPerCase = safeNumberConvert(secondaryPackageCase.split(' ')[0]);
    const caseLength=formik.values.secondaryPackagingDetails.secondaryCaseLength
    const caseLengthUnit=formik.values.secondaryPackagingDetails.secondaryCaseLengthUnit
    const caseWidth=formik.values.secondaryPackagingDetails.secondaryCaseWidth
    const caseWidthUnit=formik.values.secondaryPackagingDetails.secondaryCaseWidthUnit
    const caseHeight=formik.values.secondaryPackagingDetails.secondaryCaseHeight
    const caseHeightUnit=formik.values.secondaryPackagingDetails.secondaryCaseHeightUnit
    const caseLengthInInches=convertLength(caseLength,caseLengthUnit,'in')
    const caseWidthInInches=convertLength(caseWidth,caseWidthUnit,'in')
    const caseHeightInInches=convertLength(caseHeight,caseHeightUnit,'in')
    const secondaryPackageType=formik.values.secondaryPackagingDetails.secondaryPackageType
    let surfaceArea=0
    if(secondaryPackageType==='Wrap' || secondaryPackageType==='PAD' || secondaryPackageType==='case/pack' || secondaryPackageType==='Base' || secondaryPackageType==='CASE/Shell'){
      surfaceArea=2*((caseLengthInInches*caseWidthInInches)+(caseLengthInInches*caseHeightInInches)+(caseWidthInInches*caseHeightInInches))*0.00064516
    }else if(secondaryPackageType==='Tray' || secondaryPackageType==='TRAY & CASE'   || secondaryPackageType==='Shrink W Tray' || secondaryPackageType==='Tray -Shrink'){
      surfaceArea=((caseLengthInInches*caseWidthInInches)+2*(caseLengthInInches*caseHeightInInches)+2*(caseWidthInInches*caseHeightInInches))*0.00064516
    }else if(secondaryPackageType==='RSW +Pad' || secondaryPackageType==='Shrink' || secondaryPackageType==='Shrink W Pad' || secondaryPackageType==='RSW' || secondaryPackageType==='Paperboard' || secondaryPackageType==='Corrugated' || secondaryPackageType==='Corrugated Case' || secondaryPackageType==='Paperboard Wrap' || secondaryPackageType==='Case'){
      surfaceArea=2*((caseLengthInInches*caseWidthInInches)+(caseLengthInInches*caseHeightInInches)+(caseWidthInInches*caseHeightInInches))*0.00064516
    }else{
      surfaceArea=((caseLengthInInches*caseWidthInInches)+2*(caseLengthInInches*caseHeightInInches)+2*(caseWidthInInches*caseHeightInInches))*0.00064516
    }
    
    console.log(secondaryPackageType,'secondaryPackageTypeee',packagingData.find((item)=>item.packagingType===secondaryPackageType)?.materialGSM)
    const materialGSM=packagingData.find((item)=>item.packagingType===secondaryPackageType)?.materialGSM
    const weightOfEmptyPackage=surfaceArea*materialGSM * 0.00220462
    console.log('caseLengthInInches',caseLengthInInches)
    console.log('caseWidthInInches',caseWidthInInches)
    console.log('caseHeightInInches',caseHeightInInches)
    console.log('surfaceArea',surfaceArea.toFixed(2))
    console.log('materialGSM',materialGSM)
    console.log(weightOfEmptyPackage,'weightOfEmptyPackage')
    return (filledWeightInPounds * itemsPerCase) + weightOfEmptyPackage;
  };
  const calculateSecondaryCase = (dimension: 'Length' | 'Width' | 'Height') => {
    const diameter = safeNumberConvert(formik.values.primaryPackagingDetails.diameter);
    const diameterUnit = formik.values.primaryPackagingDetails.diameterUnit;
    const packageHeight = safeNumberConvert(formik.values.primaryPackagingDetails.packageHeight);
    const packageHeightUnit = formik.values.primaryPackagingDetails.packageHeightUnit;
    const { secondaryPackageCase, secondaryPackageType } = formik.values.secondaryPackagingDetails;

    if (!diameter || !packageHeight || !secondaryPackageCase || !secondaryPackageType) return 0;

    const [itemsPerCase, layout] = secondaryPackageCase.split(' ');
    const [rows, columns] = layout.slice(1, -1).split(/[x×]/i).map(safeNumberConvert);

    const factor = packageTypeFactor[secondaryPackageType as keyof typeof packageTypeFactor];
    console.log(factor, 'factoorrr');
    if (!factor) return 0;

    // Convert diameter to inches if in mm
    const diameterInInches = diameterUnit === 'mm' ? diameter / 25.4 : diameter;

    // Convert package height to inches if in mm
    const packageHeightInInches = packageHeightUnit === 'mm' ? packageHeight / 25.4 : packageHeight;

    if (dimension === 'Length') {
      return rows * diameterInInches + factor.length;
    } else if (dimension === 'Width') {
      return columns * diameterInInches + factor.width;
    } else {
      return packageHeightInInches + factor.height;
    }
  };

  const calculateTertiaryPackageNetWeight = () => {
    if (secondaryPkg === 'true') {
      const secondaryUnitNetWeight = safeNumberConvert(formik.values.secondaryPackagingDetails.secondaryUnitNetWeight);
      const secondaryUnitNetWeightUnit = formik.values.secondaryPackagingDetails.secondaryUnitNetWeightUnit;
      const tertiarySecondaryPackagesPerUnit = formik.values.tertiaryPackagingDetails.tertiarySecondaryPackagesPerUnit;

      if (!secondaryUnitNetWeight || !tertiarySecondaryPackagesPerUnit) return 0;

      // Convert secondary unit net weight to pounds based on unit
      let secondaryUnitNetWeightInPounds = secondaryUnitNetWeight;
      switch (secondaryUnitNetWeightUnit) {
        case 'oz':
          secondaryUnitNetWeightInPounds = secondaryUnitNetWeight / 16; // 16 oz = 1 lb
          break;
        case 'g':
          secondaryUnitNetWeightInPounds = secondaryUnitNetWeight / 453.592; // 453.592 g = 1 lb
          break;
        case 'kg':
          secondaryUnitNetWeightInPounds = secondaryUnitNetWeight * 2.20462; // 1 kg = 2.20462 lbs
          break;
        case 'lbs':
        default:
          break;
      }

      const packPerCase = safeNumberConvert(tertiarySecondaryPackagesPerUnit.split(' ')[0]);
      return secondaryUnitNetWeightInPounds * packPerCase;
    } else {
      const netContent = safeNumberConvert(formik.values.primaryPackagingDetails.netContent);
      const netContentUnit = formik.values.primaryPackagingDetails.netContentUnit;
      const tertiarySecondaryPackagesPerUnit = formik.values.tertiaryPackagingDetails.tertiarySecondaryPackagesPerUnit;

      if (!netContent || !netContentUnit || !tertiarySecondaryPackagesPerUnit) return 0;

      const netContentOz = netContentUnit === 'ml' ? netContent / 29.5735 : netContent;
      const packPerCase = safeNumberConvert(tertiarySecondaryPackagesPerUnit.split(' ')[0]);
      return netContentOz * 0.0625 * packPerCase;
    }
  };

  const calculateTertiaryPackageGrossWeight = () => {
    if (secondaryPkg === 'true') {
      const secondaryUnitGrossWeight = safeNumberConvert(formik.values.secondaryPackagingDetails.secondaryUnitGrossWeight);
      const secondaryUnitGrossWeightUnit = formik.values.secondaryPackagingDetails.secondaryUnitGrossWeightUnit;
      const tertiarySecondaryPackagesPerUnit = formik.values.tertiaryPackagingDetails.tertiarySecondaryPackagesPerUnit;

      if (!secondaryUnitGrossWeight || !tertiarySecondaryPackagesPerUnit) return 0;

      // Convert secondary unit gross weight to pounds based on unit
      let secondaryUnitGrossWeightInPounds = secondaryUnitGrossWeight;
      switch (secondaryUnitGrossWeightUnit) {
        case 'oz':
          secondaryUnitGrossWeightInPounds = secondaryUnitGrossWeight / 16; // 16 oz = 1 lb
          break;
        case 'g':
          secondaryUnitGrossWeightInPounds = secondaryUnitGrossWeight / 453.592; // 453.592 g = 1 lb
          break;
        case 'kg':
          secondaryUnitGrossWeightInPounds = secondaryUnitGrossWeight * 2.20462; // 1 kg = 2.20462 lbs
          break;
        case 'lbs':
        default:
          break;
      }

      const packPerCase = safeNumberConvert(tertiarySecondaryPackagesPerUnit.split(' ')[0]);
      return (secondaryUnitGrossWeightInPounds * packPerCase) + 0.22;
    } else {
      const filledWeight = safeNumberConvert(formik.values.primaryPackagingDetails.filledWeight);
      const tertiarySecondaryPackagesPerUnit = formik.values.tertiaryPackagingDetails.tertiarySecondaryPackagesPerUnit;

      if (!filledWeight || !tertiarySecondaryPackagesPerUnit) return 0;

      const packPerCase = safeNumberConvert(tertiarySecondaryPackagesPerUnit.split(' ')[0]);
      return (filledWeight * packPerCase) + 0.22;
    }
  };

  const calculateTertiaryCase = (dimension: 'Length' | 'Width' | 'Height') => {
    const { tertiaryPackageType, tertiarySecondaryPackagesPerUnit } = formik.values.tertiaryPackagingDetails;

    if (!tertiaryPackageType || !tertiarySecondaryPackagesPerUnit) return 0;

    const [packPerCase, layout] = tertiarySecondaryPackagesPerUnit.split(' ');
    const [rows, columns] = layout.slice(1, -1).split(/[x×]/i).map(safeNumberConvert);

    const factor = packageTypeFactor[tertiaryPackageType as keyof typeof packageTypeFactor];
    if (!factor) return 0;

    let result = 0;

    if (secondaryPkg === 'true') {
      const secondaryCaseLength = safeNumberConvert(formik.values.secondaryPackagingDetails.secondaryCaseLength);
      const secondaryCaseWidth = safeNumberConvert(formik.values.secondaryPackagingDetails.secondaryCaseWidth);
      const secondaryCaseHeight = safeNumberConvert(formik.values.secondaryPackagingDetails.secondaryCaseHeight);
      const secondaryCaseLengthUnit = formik.values.secondaryPackagingDetails.secondaryCaseLengthUnit;
      const secondaryCaseWidthUnit = formik.values.secondaryPackagingDetails.secondaryCaseWidthUnit;
      const secondaryCaseHeightUnit = formik.values.secondaryPackagingDetails.secondaryCaseHeightUnit;

      if (!secondaryCaseLength || !secondaryCaseWidth || !secondaryCaseHeight) return 0;

      // Convert to inches if in mm
      const lengthInInches = secondaryCaseLengthUnit === 'mm' ? secondaryCaseLength / 25.4 : secondaryCaseLength;
      const widthInInches = secondaryCaseWidthUnit === 'mm' ? secondaryCaseWidth / 25.4 : secondaryCaseWidth;
      const heightInInches = secondaryCaseHeightUnit === 'mm' ? secondaryCaseHeight / 25.4 : secondaryCaseHeight;

      if (dimension === 'Length') {
        result = rows * lengthInInches + factor.length;
      } else if (dimension === 'Width') {
        result = columns * widthInInches + factor.width;
      } else {
        result = heightInInches + factor.height;
      }
    } else {
      const diameter = safeNumberConvert(formik.values.primaryPackagingDetails.diameter);
      const packageHeight = safeNumberConvert(formik.values.primaryPackagingDetails.packageHeight);
      const diameterUnit = formik.values.primaryPackagingDetails.diameterUnit;
      const packageHeightUnit = formik.values.primaryPackagingDetails.packageHeightUnit;

      if (!diameter || !packageHeight) return 0;

      // Convert to inches if in mm
      const diameterInInches = diameterUnit === 'mm' ? diameter / 25.4 : diameter;
      const heightInInches = packageHeightUnit === 'mm' ? packageHeight / 25.4 : packageHeight;

      if (dimension === 'Length') {
        result = rows * diameterInInches + factor.length;
      } else if (dimension === 'Width') {
        result = columns * diameterInInches + factor.width;
      } else {
        result = heightInInches + factor.height;
      }
    }

    return result;
  };
  useEffect(() => {
    if (secondaryPkg === 'true') {
      const secondaryNetWeight = calculateSecondaryPackageNetWeight();
      const secondaryNetWeightUnit = formik.values.secondaryPackagingDetails.secondaryUnitNetWeightUnit || 'lbs';
      const convertedNetWeight = convertWeight(secondaryNetWeight, 'lbs', secondaryNetWeightUnit);
      if (formik.values.secondaryPackagingDetails.isUnitNetWeightAutoCalculated) {
        formik.setFieldValue('secondaryPackagingDetails.secondaryUnitNetWeight', safeToFixed(convertedNetWeight));
      }

      // setIsSecondaryNetWeightAutoCalculated(true);
      // formik.setFieldValue('secondaryPackagingDetails.isUnitNetWeightAutoCalculated', true);

      const secondaryGrossWeight = calculateSecondaryPackageGrossWeight();
      const secondaryGrossWeightUnit = formik.values.secondaryPackagingDetails.secondaryUnitGrossWeightUnit || 'lbs';
      const convertedGrossWeight = convertWeight(secondaryGrossWeight, 'lbs', secondaryGrossWeightUnit);
      if (formik.values.secondaryPackagingDetails.isUnitGrossWeightAutoCalculated) {
        formik.setFieldValue('secondaryPackagingDetails.secondaryUnitGrossWeight', safeToFixed(convertedGrossWeight));
      }
      // setIsSecondaryGrossWeightAutoCalculated(true);

      const secondaryCaseLength = calculateSecondaryCase('Length');
      const secondaryCaseLengthUnit = formik.values.secondaryPackagingDetails.secondaryCaseLengthUnit || 'inch';
      const convertedLength = convertLength(secondaryCaseLength, 'inch', secondaryCaseLengthUnit);
      if (formik.values.secondaryPackagingDetails.isCaseLengthAutoCalculated) {
        formik.setFieldValue('secondaryPackagingDetails.secondaryCaseLength', safeToFixed(convertedLength));
      }
      // setIsSecondaryCaseLengthAutoCalculated(true);

      const secondaryCaseWidth = calculateSecondaryCase('Width');
      const secondaryCaseWidthUnit = formik.values.secondaryPackagingDetails.secondaryCaseWidthUnit || 'inch';
      const convertedWidth = convertLength(secondaryCaseWidth, 'inch', secondaryCaseWidthUnit);
      if (formik.values.secondaryPackagingDetails.isCaseWidthAutoCalculated) {
        formik.setFieldValue('secondaryPackagingDetails.secondaryCaseWidth', safeToFixed(convertedWidth));
      }
      // setIsSecondaryCaseWidthAutoCalculated(true);

      const secondaryCaseHeight = calculateSecondaryCase('Height');
      const secondaryCaseHeightUnit = formik.values.secondaryPackagingDetails.secondaryCaseHeightUnit || 'inch';
      const convertedHeight = convertLength(secondaryCaseHeight, 'inch', secondaryCaseHeightUnit);
      if (formik.values.secondaryPackagingDetails.isCaseHeightAutoCalculated) {
        formik.setFieldValue('secondaryPackagingDetails.secondaryCaseHeight', safeToFixed(convertedHeight));
      }
      // setIsSecondaryCaseHeightAutoCalculated(true);
    }
  }, [formik.values.primaryPackagingDetails, formik.values.secondaryPackagingDetails.secondaryPackageCase, formik.values.secondaryPackagingDetails.secondaryPackageType]);
  useEffect(()=>{
   if(formik.values.secondaryPackagingDetails.secondaryPkg==='true'){
    const secondaryGrossWeight = calculateSecondaryPackageGrossWeight();
    const secondaryGrossWeightUnit = formik.values.secondaryPackagingDetails.secondaryUnitGrossWeightUnit || 'lbs';
    const convertedGrossWeight = convertWeight(secondaryGrossWeight, 'lbs', secondaryGrossWeightUnit);
    if (formik.values.secondaryPackagingDetails.isUnitGrossWeightAutoCalculated) {
      formik.setFieldValue('secondaryPackagingDetails.secondaryUnitGrossWeight', safeToFixed(convertedGrossWeight));
    }
   }
  },[formik.values.secondaryPackagingDetails.secondaryPkg,formik.values.secondaryPackagingDetails.secondaryPackageType,
    formik.values.secondaryPackagingDetails.secondaryCaseLength,formik.values.secondaryPackagingDetails.secondaryCaseWidth,formik.values.secondaryPackagingDetails.secondaryCaseHeight
  ])
  useEffect(() => {
    if (tertiaryPkg === 'true') {
      const tertiaryNetWeight = calculateTertiaryPackageNetWeight();
      const tertiaryNetWeightUnit = formik.values.tertiaryPackagingDetails.tertiaryUnitNetWeightUnit || 'lbs';
      const convertedNetWeight = convertWeight(tertiaryNetWeight, 'lbs', tertiaryNetWeightUnit);
      if (formik.values.tertiaryPackagingDetails.isTerUnitNetWeightAutoCalculated) {
        formik.setFieldValue('tertiaryPackagingDetails.tertiaryUnitNetWeight', safeToFixed(convertedNetWeight));
      }
      // setIsTertiaryNetWeightAutoCalculated(true);

      const tertiaryGrossWeight = calculateTertiaryPackageGrossWeight();
      const tertiaryGrossWeightUnit = formik.values.tertiaryPackagingDetails.tertiaryUnitGrossWeightUnit || 'lbs';
      const convertedGrossWeight = convertWeight(tertiaryGrossWeight, 'lbs', tertiaryGrossWeightUnit);
      if (formik.values.tertiaryPackagingDetails.isTerUnitGrossWeightAutoCalculated) {
        formik.setFieldValue('tertiaryPackagingDetails.tertiaryUnitGrossWeight', safeToFixed(convertedGrossWeight));
      }
      // setIsTertiaryGrossWeightAutoCalculated(true);

      const tertiaryCaseLength = calculateTertiaryCase('Length');
      const tertiaryCaseLengthUnit = formik.values.tertiaryPackagingDetails.tertiaryCaseLengthUnit || 'inch';
      const convertedLength = convertLength(tertiaryCaseLength, 'inch', tertiaryCaseLengthUnit);
      if (formik.values.tertiaryPackagingDetails.isTerCaseLengthAutoCalculated) {
        formik.setFieldValue('tertiaryPackagingDetails.tertiaryCaseLength', safeToFixed(convertedLength));
      }
      // setIsTertiaryCaseLengthAutoCalculated(true);

      const tertiaryCaseWidth = calculateTertiaryCase('Width');
      const tertiaryCaseWidthUnit = formik.values.tertiaryPackagingDetails.tertiaryCaseWidthUnit || 'inch';
      const convertedWidth = convertLength(tertiaryCaseWidth, 'inch', tertiaryCaseWidthUnit);
      if (formik.values.tertiaryPackagingDetails.isTerCaseWidthAutoCalculated) {
        formik.setFieldValue('tertiaryPackagingDetails.tertiaryCaseWidth', safeToFixed(convertedWidth));
      }
      // setIsTertiaryCaseWidthAutoCalculated(true);

      const tertiaryCaseHeight = calculateTertiaryCase('Height');
      const tertiaryCaseHeightUnit = formik.values.tertiaryPackagingDetails.tertiaryCaseHeightUnit || 'inch';
      const convertedHeight = convertLength(tertiaryCaseHeight, 'inch', tertiaryCaseHeightUnit);
      if (formik.values.tertiaryPackagingDetails.isTerCaseHeightAutoCalculated) {
        formik.setFieldValue('tertiaryPackagingDetails.tertiaryCaseHeight', safeToFixed(convertedHeight));
      }
      // setIsTertiaryCaseHeightAutoCalculated(true);
    }
  }, [formik.values.primaryPackagingDetails, formik.values.secondaryPackagingDetails, formik.values.tertiaryPackagingDetails.tertiarySecondaryPackagesPerUnit, formik.values.tertiaryPackagingDetails.tertiaryPackageType, secondaryPkg, tertiaryPkg]);

  const handleSecondaryPkgChange = (value: string) => {
    formik.setFieldValue("secondaryPackagingDetails.secondaryPkg", value, false);
    if (value === "false") {
      formik.setValues({
        ...formik.values,
        secondaryPackagingDetails: {
          ...formik.values.secondaryPackagingDetails,
          secondaryPkg: "false",
        },
      }, false);
    } else {
      formik.setValues({
        ...formik.values,
        secondaryPackagingDetails: {
          ...formik.values.secondaryPackagingDetails,
          secondaryPkg: "true",
        },

      }, false);
    }
  };

  const handleTertiaryPkgChange = (value: string) => {
    formik.setFieldValue("tertiaryPackagingDetails.tertiaryPkg", value, false);
    if (value === "false") {
      formik.setValues({
        ...formik.values,
        tertiaryPackagingDetails: {
          ...formik.values.tertiaryPackagingDetails,
          tertiaryPkg: "false",
        },
      }, false);
    } else {
      formik.setValues({
        ...formik.values,
        tertiaryPackagingDetails: {
          ...formik.values.tertiaryPackagingDetails,
          tertiaryPkg: "true",
        },
      }, false);
    }
  };


  return (
    <div className="w-full">
      <div className="w-full pt-5">
        <div className="flex-center border-border-style-1 border w-full">
          <div className="label-bg">
            <Label className="label lg:text-sm text-[9px] font-bold flex items-center">
              Secondary Pkg (Yes/No)*
              <InfoIcon
                className="h-4 w-4 text-muted-foreground cursor-pointer ml-1 hover:text-blue"
                onClick={() => setIsSecondaryImageOpen(true)}
              />
            </Label>
          </div>
          {secondaryPkg === "true" && (
            <>
              <div className="label-bg">
                <Label className="label lg:text-sm text-[9px] font-bold">Secondary Package Type*</Label>
              </div>
              <div className="label-bg">
                <Label className="label lg:text-sm text-[9px] font-bold">Items/Case*</Label>
              </div>
            </>
          )}
        </div>

        <div className="flex-center border-border-style-1 border w-full">
          <div className="relative w-full">
            <Select
              name="secondaryPackagingDetails.secondaryPkg"
              value={secondaryPkg}
              onValueChange={handleSecondaryPkgChange}
            >
              <SelectTrigger
                className={cn(
                  "w-full rounded-none  bg-selectBlue border-required",
                  formik.touched.secondaryPackagingDetails?.secondaryPkg &&
                  formik.errors.secondaryPackagingDetails?.secondaryPkg &&
                  "border-2 last:border-l-0 border-states-error outline-none"
                )}
              >
                <SelectValue placeholder="Secondary Pkg (Yes/No)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Yes</SelectItem>
                <SelectItem value="false">No</SelectItem>
              </SelectContent>
            </Select>
            {formik.touched.secondaryPackagingDetails?.secondaryPkg &&
              formik.errors.secondaryPackagingDetails?.secondaryPkg && (
                <AlertTooltip
                  message={formik.errors.secondaryPackagingDetails.secondaryPkg as string}
                />
              )}
          </div>

          {secondaryPkg === "true" && (
            <>
              <div className="relative w-full">
                <Select
                  name="secondaryPackagingDetails.secondaryPackageType"
                  value={formik.values.secondaryPackagingDetails.secondaryPackageType}
                  onValueChange={(value) => formik.setFieldValue("secondaryPackagingDetails.secondaryPackageType", value)}
                >
                  <SelectTrigger className="w-full bg-selectBlue border-2 border-required">
                    <SelectValue placeholder="Select Secondary Package Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {secondaryPackageTypeOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formik.touched.secondaryPackagingDetails?.secondaryPackageType &&
                  formik.errors.secondaryPackagingDetails?.secondaryPackageType && (
                    <AlertTooltip
                      message={formik.errors.secondaryPackagingDetails.secondaryPackageType as string}
                    />
                  )}
              </div>

              <div className="relative w-full">
                <Select
                  name="secondaryPackagingDetails.secondaryPackageCase"
                  value={formik.values.secondaryPackagingDetails.secondaryPackageCase}
                  onValueChange={(value) => formik.setFieldValue(`secondaryPackagingDetails.secondaryPackageCase`, value)}
                >
                  <SelectTrigger className="w-full bg-selectBlue border-2 border-required">
                    <SelectValue placeholder="Select Package Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {itemsInCase.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formik.touched.secondaryPackagingDetails?.secondaryPackageCase &&
                  formik.errors.secondaryPackagingDetails?.secondaryPackageCase && (
                    <AlertTooltip
                      message={formik.errors.secondaryPackagingDetails.secondaryPackageCase as string}
                    />
                  )}
              </div>
            </>
          )}
        </div>

        {secondaryPkg === "true" && (
          <>
            <div className="flex-center border-border-style-1 border w-full">
              <div className="label-bg flex items-center">
                <Label className="label lg:text-sm text-[9px]  font-bold flex-grow flex items-center">
                  Secondary Package Net Weight (OD)*
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help ml-1" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Formula: Net content in OZ * 0.0625 * Items/Case</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <div className="flex items-center gap-1">
                  {!formik.values.secondaryPackagingDetails.isUnitNetWeightAutoCalculated && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            title="Reset to auto-calculated value"
                            onClick={() => {
                              const calculatedValue = calculateSecondaryPackageNetWeight();
                              formik.setFieldValue('secondaryPackagingDetails.secondaryUnitNetWeight', safeToFixed(calculatedValue));
                              // setIsSecondaryNetWeightAutoCalculated(true);
                              formik.setFieldValue('secondaryPackagingDetails.isUnitNetWeightAutoCalculated', true);
                              setHasUnsavedChanges(true);
                            }}
                            className="p-1 hover:bg-gray-100 rounded-full"
                          >
                            <RotateCcw className="h-3 w-3 text-gray-500" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Reset to auto-calculated value</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {renderUnitSelect("secondaryUnitNetWeight", ["lbs", "oz", 'g', 'kg'])}
                </div>
              </div>
              <div className="label-bg flex items-center">
                <Label className="label lg:text-sm text-[9px] font-bold flex-grow flex items-center">
                  Secondary Package Gross Weight (OD)*
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help ml-1" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Formula: (Filled Weight in pounds * Items/Case) + 0.22</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <div className="flex items-center gap-1">
                  {!formik.values.secondaryPackagingDetails.isUnitGrossWeightAutoCalculated && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            title="Reset to auto-calculated value"
                            onClick={() => {
                              const calculatedValue = calculateSecondaryPackageGrossWeight();
                              formik.setFieldValue('secondaryPackagingDetails.secondaryUnitGrossWeight', safeToFixed(calculatedValue));
                              // setIsSecondaryGrossWeightAutoCalculated(true);
                              formik.setFieldValue('secondaryPackagingDetails.isUnitGrossWeightAutoCalculated', true);
                              setHasUnsavedChanges(true);
                            }}
                            className="p-1 hover:bg-gray-100 rounded-full"
                          >
                            <RotateCcw className="h-3 w-3 text-gray-500" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Reset to auto-calculated value</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {renderUnitSelect("secondaryUnitGrossWeight", ["lbs", "oz", 'g', 'kg'])}
                </div>
              </div>
              <div className="label-bg flex items-center">
                <Label className="label lg:text-sm text-[9px] font-bold flex-grow flex items-center">
                  Secondary Case Length (OD)*
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help ml-1" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Formula: Rows in items per case * Diameter(in) + Secondary package type contribution</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <div className="flex items-center gap-1">
                  {!formik.values.secondaryPackagingDetails.isCaseLengthAutoCalculated && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            title="Reset to auto-calculated value"
                            onClick={() => {
                              const calculatedValue = calculateSecondaryCase('Length');
                              formik.setFieldValue('secondaryPackagingDetails.secondaryCaseLength', safeToFixed(calculatedValue));
                              // setIsSecondaryCaseLengthAutoCalculated(true);
                              formik.setFieldValue('secondaryPackagingDetails.isCaseLengthAutoCalculated', true);
                              setHasUnsavedChanges(true);
                            }}
                            className="p-1 hover:bg-gray-100 rounded-full"
                          >
                            <RotateCcw className="h-3 w-3 text-gray-500" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Reset to auto-calculated value</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {renderUnitSelect("secondaryCaseLength", ["inch", "mm"])}
                </div>
              </div>
            </div>

            <div className="flex-center border-border-style-1 border w-full">
              <div className="relative w-full">
                <Input
                  name="secondaryPackagingDetails.secondaryUnitNetWeight"
                  placeholder="Enter"
                  type="number"
                  value={formik.values.secondaryPackagingDetails.secondaryUnitNetWeight}
                  onChange={(e) => {
                    formik.handleChange(e);
                    // setIsSecondaryNetWeightAutoCalculated(false);
                    formik.setFieldValue('secondaryPackagingDetails.isUnitNetWeightAutoCalculated', false);
                  }}
                  onBlur={formik.handleBlur}
                  className={cn(
                    'border-2 border-required',
                    formik.values.secondaryPackagingDetails.isUnitNetWeightAutoCalculated && "bg-bgYellow"
                  )}
                />
                {formik.touched.secondaryPackagingDetails?.secondaryUnitNetWeight &&
                  formik.errors.secondaryPackagingDetails?.secondaryUnitNetWeight && (
                    <AlertTooltip
                      message={formik.errors.secondaryPackagingDetails.secondaryUnitNetWeight as string}
                    />
                  )}
              </div>

              <div className="relative w-full">
                <Input
                  name="secondaryPackagingDetails.secondaryUnitGrossWeight"
                  placeholder="Enter"
                  type="number"
                  value={formik.values.secondaryPackagingDetails.secondaryUnitGrossWeight}
                  onChange={(e) => {
                    formik.handleChange(e);
                    formik.setFieldValue('secondaryPackagingDetails.isUnitGrossWeightAutoCalculated', false);
                  }}
                  onBlur={formik.handleBlur}
                  className={cn(
                    'border-2 border-required',
                    formik.values.secondaryPackagingDetails.isUnitGrossWeightAutoCalculated && "bg-bgYellow"
                  )}
                />
                {formik.touched.secondaryPackagingDetails?.secondaryUnitGrossWeight &&
                  formik.errors.secondaryPackagingDetails?.secondaryUnitGrossWeight && (
                    <AlertTooltip
                      message={formik.errors.secondaryPackagingDetails.secondaryUnitGrossWeight as string}
                    />
                  )}
              </div>

              <div className="relative w-full">
                <Input
                  name="secondaryPackagingDetails.secondaryCaseLength"
                  placeholder="Enter"
                  type="number"
                  value={formik.values.secondaryPackagingDetails.secondaryCaseLength}
                  onChange={(e) => {
                    formik.handleChange(e);
                    // setIsSecondaryCaseLengthAutoCalculated(false);
                    formik.setFieldValue('secondaryPackagingDetails.isCaseLengthAutoCalculated', false);
                  }}
                  onBlur={formik.handleBlur}
                  className={cn(
                    'border-2 border-required',
                    formik.values.secondaryPackagingDetails.isCaseLengthAutoCalculated && "bg-bgYellow"
                  )}
                />
                {formik.touched.secondaryPackagingDetails?.secondaryCaseLength &&
                  formik.errors.secondaryPackagingDetails?.secondaryCaseLength && (
                    <AlertTooltip
                      message={formik.errors.secondaryPackagingDetails.secondaryCaseLength as string}
                    />
                  )}
              </div>
            </div>

            <div className="flex-center border-border-style-1 border w-full">
              <div className="label-bg w-full flex items-center">
                <Label className="label lg:text-sm text-[9px] font-bold flex-grow flex items-center">
                  Secondary Case Width (OD)*
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help ml-1" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Formula: Columns in items per case * Diameter(in) + Secondary package type factor</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <div className="flex items-center gap-1">
                  {!formik.values.secondaryPackagingDetails.isCaseWidthAutoCalculated && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            title="Reset to auto-calculated value"
                            onClick={() => {
                              const calculatedValue = calculateSecondaryCase('Width');
                              formik.setFieldValue('secondaryPackagingDetails.secondaryCaseWidth', safeToFixed(calculatedValue));
                              // setIsSecondaryCaseWidthAutoCalculated(true);
                              formik.setFieldValue('secondaryPackagingDetails.isCaseWidthAutoCalculated', true);
                              setHasUnsavedChanges(true);
                            }}
                            className="p-1 hover:bg-gray-100 rounded-full"
                          >
                            <RotateCcw className="h-3 w-3 text-gray-500" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Reset to auto-calculated value</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {renderUnitSelect("secondaryCaseWidth", ["inch", "mm"])}
                </div>
              </div>
              <div className="label-bg w-full flex items-center">
                <Label className="label lg:text-sm text-[9px] font-bold flex-grow flex items-center">
                  Secondary Case Height (OD)*
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help ml-1" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Formula: Package Height(in) + Secondary package type factor</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <div className="flex items-center gap-1">
                  {!formik.values.secondaryPackagingDetails.isCaseHeightAutoCalculated && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            title="Reset to auto-calculated value"
                            onClick={() => {
                              const calculatedValue = calculateSecondaryCase('Height');
                              formik.setFieldValue('secondaryPackagingDetails.secondaryCaseHeight', safeToFixed(calculatedValue));
                              // setIsSecondaryCaseHeightAutoCalculated(true);
                              formik.setFieldValue('secondaryPackagingDetails.isCaseHeightAutoCalculated', true);
                              setHasUnsavedChanges(true);
                            }}
                            className="p-1 hover:bg-gray-100 rounded-full"
                          >
                            <RotateCcw className="h-3 w-3 text-gray-500" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Reset to auto-calculated value</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {renderUnitSelect("secondaryCaseHeight", ["inch", "mm"])}
                </div>
              </div>
              <div className="label-bg w-full">
                <Label className="label lg:text-sm text-[9px] font-bold">
                  Filled Case Compression (lbf)
                </Label>
              </div>
            </div>
            <div className="flex-center border-border-style-1 border w-full">
              <div className="relative w-full">
                <Input
                  name="secondaryPackagingDetails.secondaryCaseWidth"
                  placeholder="Enter"
                  type="number"
                  value={formik.values.secondaryPackagingDetails.secondaryCaseWidth}
                  onChange={(e) => {
                    formik.handleChange(e);
                    formik.setFieldValue('secondaryPackagingDetails.isCaseWidthAutoCalculated', false);
                  }}
                  onBlur={formik.handleBlur}
                  className={cn(
                    'border-2 border-required',
                    formik.values.secondaryPackagingDetails.isCaseWidthAutoCalculated && "bg-bgYellow"
                  )}
                />
                {formik.touched.secondaryPackagingDetails?.secondaryCaseWidth &&
                  formik.errors.secondaryPackagingDetails?.secondaryCaseWidth && (
                    <AlertTooltip
                      message={formik.errors.secondaryPackagingDetails.secondaryCaseWidth as string}
                    />
                  )}
              </div>

              <div className="relative w-full">
                <Input
                  name="secondaryPackagingDetails.secondaryCaseHeight"
                  placeholder="Enter"
                  type="number"
                  value={formik.values.secondaryPackagingDetails.secondaryCaseHeight}
                  onChange={(e) => {
                    formik.handleChange(e);
                    formik.setFieldValue('secondaryPackagingDetails.isCaseHeightAutoCalculated', false);
                  }}
                  onBlur={formik.handleBlur}
                  className={cn(
                    'border-2 border-required',
                    formik.values.secondaryPackagingDetails.isCaseHeightAutoCalculated && "bg-bgYellow"
                  )}
                />
                {formik.touched.secondaryPackagingDetails?.secondaryCaseHeight &&
                  formik.errors.secondaryPackagingDetails?.secondaryCaseHeight && (
                    <AlertTooltip
                      message={formik.errors.secondaryPackagingDetails.secondaryCaseHeight as string}
                    />
                  )}
              </div>

              <div className="relative w-full">
                <Input
                  name="secondaryPackagingDetails.filledCaseCompression"
                  placeholder="Enter"
                  type="number"
                  value={formik.values.secondaryPackagingDetails.filledCaseCompression}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={cn(
                    formik.touched.secondaryPackagingDetails?.filledCaseCompression &&
                    formik.errors.secondaryPackagingDetails?.filledCaseCompression &&
                    "border-2 last:border-l-0 border-states-error outline-none"
                  )}
                />
                {formik.touched.secondaryPackagingDetails?.filledCaseCompression &&
                  formik.errors.secondaryPackagingDetails?.filledCaseCompression && (
                    <AlertTooltip
                      message={formik.errors.secondaryPackagingDetails.filledCaseCompression as string}
                    />
                  )}
              </div>
            </div>
          </>
        )}
      </div>
    { formik.values.secondaryPackagingDetails.secondaryPkg==='true' && <>
      <div className="  border">
            <div className="label-bg ">
                <Label className="label lg:text-sm text-[9px] font-bold">
                Gauge/Thickness*
                </Label>
              </div>
            </div>
            <div className=" border-border-style-1 border w-full">
              <div className="relative ">
                <Input
                  name="secondaryPackagingDetails.gauge"
                  placeholder="Enter"
                  type="number"
                  value={formik.values.secondaryPackagingDetails.gauge}
                  onChange={(e) => {
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  className={cn(
                    'border-2 border-required',
                  )}
                />
                {formik.touched.secondaryPackagingDetails?.gauge &&
                  formik.errors.secondaryPackagingDetails?.gauge && (
                    <AlertTooltip
                      message={formik.errors.secondaryPackagingDetails.gauge as string}
                    />
                  )}
              </div>
</div>
      </>}

      <Dialog open={isSecondaryImageOpen} onOpenChange={setIsSecondaryImageOpen}>
        <DialogContent className="max-w-[90vw] min-w-[60vw] max-h-[90vh] w-auto">
          <img src={"/pallet-config/secondary-pallet.jpg"} alt="Secondary Package" className="w-full h-full object-contain" />
        </DialogContent>
      </Dialog>
      {/* Tertiary Packaging Details */}
      <div className="w-full pt-5">
        <div className="flex-center border-border-style-1 border w-full">
          <div className="label-bg">
            <Label className="label lg:text-sm text-[9px] font-bold flex items-center">
              Tertiary Pkg (Yes/No)*
              <InfoIcon
                className="h-4 w-4 text-muted-foreground cursor-pointer ml-1 hover:text-blue"
                onClick={() => setIsTertiaryImageOpen(true)}
              />
            </Label>
          </div>
          {tertiaryPkg === "true" && (
            <>
              <div className="label-bg">
                <Label className="label lg:text-sm text-[9px] font-bold">Tertiary Package Type</Label>
              </div>
              <div className="label-bg">
                <Label className="label lg:text-sm text-[9px] font-bold">
                  Pack/Case
                </Label>
              </div>
            </>
          )}
        </div>

        <div className="flex-center border-border-style-1 border w-full">
          <div className="relative w-full">
            <Select
              name="tertiaryPackagingDetails.tertiaryPkg"
              value={tertiaryPkg}
              onValueChange={handleTertiaryPkgChange}
            >
              <SelectTrigger
                className={cn(
                  "w-full rounded-none bg-transparent  bg-selectBlue border-required",
                  formik.touched.tertiaryPackagingDetails?.tertiaryPkg &&
                  formik.errors.tertiaryPackagingDetails?.tertiaryPkg &&
                  "border-2 last:border-l-0 border-states-error outline-none"
                )}
              >
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Yes</SelectItem>
                <SelectItem value="false">No</SelectItem>
              </SelectContent>
            </Select>
            {formik.touched.tertiaryPackagingDetails?.tertiaryPkg &&
              formik.errors.tertiaryPackagingDetails?.tertiaryPkg && (
                <AlertTooltip
                  message={formik.errors.tertiaryPackagingDetails.tertiaryPkg as string}
                />
              )}
          </div>

          {tertiaryPkg === "true" && (
            <>
              <div className="relative w-full">
                <Select
                  name="tertiaryPackagingDetails.tertiaryPackageType"
                  value={formik.values.tertiaryPackagingDetails.tertiaryPackageType}
                  onValueChange={(value) => formik.setFieldValue("tertiaryPackagingDetails.tertiaryPackageType", value)}
                >
                  <SelectTrigger className="w-full bg-selectBlue">
                    <SelectValue placeholder="Select Tertiary Package Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {tertiaryPackageTypeOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formik.touched.tertiaryPackagingDetails?.tertiaryPackageType &&
                  formik.errors.tertiaryPackagingDetails?.tertiaryPackageType && (
                    <AlertTooltip
                      message={formik.errors.tertiaryPackagingDetails.tertiaryPackageType as string}
                    />
                  )}
              </div>

              <div className="relative w-full">
                <Select
                  name="tertiaryPackagingDetails.tertiarySecondaryPackagesPerUnit"
                  value={formik.values.tertiaryPackagingDetails.tertiarySecondaryPackagesPerUnit}
                  onValueChange={(value) => {
                    formik.setFieldValue(`tertiaryPackagingDetails.tertiarySecondaryPackagesPerUnit`, value)
                  }}
                >
                  <SelectTrigger className="w-full bg-selectBlue">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {itemsInCase.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formik.touched.tertiaryPackagingDetails?.tertiarySecondaryPackagesPerUnit &&
                  formik.errors.tertiaryPackagingDetails?.tertiarySecondaryPackagesPerUnit && (
                    <AlertTooltip
                      message={formik.errors.tertiaryPackagingDetails.tertiarySecondaryPackagesPerUnit as string}
                    />
                  )}
              </div>
            </>
          )}
        </div>

        {tertiaryPkg === "true" && (
          <>
            <div className="flex-center border-border-style-1 border w-full">
              <div className="label-bg flex items-center">
                <Label className="label lg:text-sm text-[9px] font-bold flex-grow flex items-center">
                  Tertiary Package Net Weight (OD)
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help ml-1" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Formula: {secondaryPkg === 'true' ? 'Secondary package net weight(pounds) * Pack/Case' : 'Net content in OZ * 0.0625 * Pack/Case'}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <div className="flex items-center gap-1">
                  {!formik.values.tertiaryPackagingDetails.isTerUnitNetWeightAutoCalculated && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            title="Reset to auto-calculated value"
                            onClick={() => {
                              const calculatedValue = calculateTertiaryPackageNetWeight();
                              formik.setFieldValue('tertiaryPackagingDetails.tertiaryUnitNetWeight', safeToFixed(calculatedValue));
                              formik.setFieldValue('tertiaryPackagingDetails.isTerUnitNetWeightAutoCalculated', true);
                              setHasUnsavedChanges(true);
                            }}
                            className="p-1 hover:bg-gray-100 rounded-full"
                          >
                            <RotateCcw className="h-3 w-3 text-gray-500" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Reset to auto-calculated value</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {renderUnitSelect("tertiaryUnitNetWeight", ["lbs", "oz", 'g', 'kg'])}
                </div>
              </div>
              <div className="label-bg flex items-center">
                <Label className="label lg:text-sm text-[9px] font-bold flex-grow flex items-center">
                  Tertiary Package Gross Weight (OD)
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help ml-1" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Formula: {secondaryPkg === 'true' ? '(Secondary package gross weight(pounds) * Pack/Case) + 0.22' : '(Filled Weight in pounds * Pack/Case) + 0.22'}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <div className="flex items-center gap-1">
                  {!formik.values.tertiaryPackagingDetails.isTerUnitGrossWeightAutoCalculated && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            title="Reset to auto-calculated value"
                            onClick={() => {
                              const calculatedValue = calculateTertiaryPackageGrossWeight();
                              formik.setFieldValue('tertiaryPackagingDetails.tertiaryUnitGrossWeight', safeToFixed(calculatedValue));
                              formik.setFieldValue('tertiaryPackagingDetails.isTerUnitGrossWeightAutoCalculated', true);
                              setHasUnsavedChanges(true);
                            }}
                            className="p-1 hover:bg-gray-100 rounded-full"
                          >
                            <RotateCcw className="h-3 w-3 text-gray-500" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Reset to auto-calculated value</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {renderUnitSelect("tertiaryUnitGrossWeight", ["lbs", "oz", 'g', 'kg'])}
                </div>
              </div>
              <div className="label-bg flex items-center">
                <Label className="label lg:text-sm text-[9px] font-bold flex-grow flex items-center">
                  Tertiary Case Length (OD)
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help ml-1" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Formula: {secondaryPkg === 'true' ? 'Rows in Pack/Case * Secondary Case Length(in) + Tertiary Package factor' : 'Rows in Pack/Case * Diameter(in) + Tertiary Package factor'}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <div className="flex items-center gap-1">
                  {!formik.values.tertiaryPackagingDetails.isTerCaseLengthAutoCalculated && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            title="Reset to auto-calculated value"
                            onClick={() => {
                              const calculatedValue = calculateTertiaryCase('Length');
                              formik.setFieldValue('tertiaryPackagingDetails.tertiaryCaseLength', safeToFixed(calculatedValue));
                              formik.setFieldValue('tertiaryPackagingDetails.isTerCaseLengthAutoCalculated', true);
                              setHasUnsavedChanges(true);
                            }}
                            className="p-1 hover:bg-gray-100 rounded-full"
                          >
                            <RotateCcw className="h-3 w-3 text-gray-500" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Reset to auto-calculated value</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {renderUnitSelect("tertiaryCaseLength", ["inch", "mm"])}
                </div>
              </div>
            </div>

            <div className="flex-center border-border-style-1 border w-full">
              <div className="relative w-full">
                <Input
                  name="tertiaryPackagingDetails.tertiaryUnitNetWeight"
                  placeholder="Enter"
                  type="number"
                  value={formik.values.tertiaryPackagingDetails.tertiaryUnitNetWeight}
                  onChange={(e) => {
                    formik.handleChange(e);
                    formik.setFieldValue('tertiaryPackagingDetails.isTerUnitNetWeightAutoCalculated', false);
                  }}
                  onBlur={formik.handleBlur}
                  className={cn(
                    formik.touched.tertiaryPackagingDetails?.tertiaryUnitNetWeight &&
                    formik.errors.tertiaryPackagingDetails?.tertiaryUnitNetWeight &&
                    "border-2 last:border-l-0 border-states-error outline-none",
                    formik.values.tertiaryPackagingDetails.isTerUnitNetWeightAutoCalculated && "bg-bgYellow"
                  )}
                />
                {formik.touched.tertiaryPackagingDetails?.tertiaryUnitNetWeight &&
                  formik.errors.tertiaryPackagingDetails?.tertiaryUnitNetWeight && (
                    <AlertTooltip
                      message={formik.errors.tertiaryPackagingDetails.tertiaryUnitNetWeight as string}
                    />
                  )}
              </div>

              <div className="relative w-full">
                <Input
                  name="tertiaryPackagingDetails.tertiaryUnitGrossWeight"
                  placeholder="Enter"
                  type="number"
                  value={formik.values.tertiaryPackagingDetails.tertiaryUnitGrossWeight}
                  onChange={(e) => {
                    formik.handleChange(e);
                    formik.setFieldValue('tertiaryPackagingDetails.isTerUnitGrossWeightAutoCalculated', false);
                  }}
                  onBlur={formik.handleBlur}
                  className={cn(
                    formik.touched.tertiaryPackagingDetails?.tertiaryUnitGrossWeight &&
                    formik.errors.tertiaryPackagingDetails?.tertiaryUnitGrossWeight &&
                    "border-2 last:border-l-0 border-states-error outline-none",
                    formik.values.tertiaryPackagingDetails.isTerUnitGrossWeightAutoCalculated && "bg-bgYellow"
                  )}
                />
                {formik.touched.tertiaryPackagingDetails?.tertiaryUnitGrossWeight &&
                  formik.errors.tertiaryPackagingDetails?.tertiaryUnitGrossWeight && (
                    <AlertTooltip
                      message={formik.errors.tertiaryPackagingDetails.tertiaryUnitGrossWeight as string}
                    />
                  )}
              </div>

              <div className="relative w-full">
                <Input
                  name="tertiaryPackagingDetails.tertiaryCaseLength"
                  placeholder="Enter"
                  type="number"
                  value={formik.values.tertiaryPackagingDetails.tertiaryCaseLength}
                  onChange={(e) => {
                    formik.handleChange(e);
                    formik.setFieldValue('tertiaryPackagingDetails.isTerCaseLengthAutoCalculated', false);
                  }}
                  onBlur={formik.handleBlur}
                  className={cn(
                    formik.touched.tertiaryPackagingDetails?.tertiaryCaseLength &&
                    formik.errors.tertiaryPackagingDetails?.tertiaryCaseLength &&
                    "border-2 last:border-l-0 border-states-error outline-none",
                    formik.values.tertiaryPackagingDetails.isTerCaseLengthAutoCalculated && "bg-bgYellow"
                  )}
                />
                {formik.touched.tertiaryPackagingDetails?.tertiaryCaseLength &&
                  formik.errors.tertiaryPackagingDetails?.tertiaryCaseLength && (
                    <AlertTooltip
                      message={formik.errors.tertiaryPackagingDetails.tertiaryCaseLength as string}
                    />
                  )}
              </div>
            </div>

            <div className="flex  border-border-style-1 border w-full">
              <div className="label-bg w-1/3 flex items-center">
                <Label className="label lg:text-sm text-[9px] font-bold flex-grow flex items-center">
                  Tertiary Case Width (OD)
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help ml-1" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Formula: {secondaryPkg === 'true' ? 'Columns in Pack/Case * Secondary Case Width(in) + Tertiary Package factor' : 'Columns in Pack/Case * Diameter(in) + Tertiary Package factor'}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <div className="flex items-center gap-1">
                  {!formik.values.tertiaryPackagingDetails.isTerCaseWidthAutoCalculated && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            title="Reset to auto-calculated value"
                            onClick={() => {
                              const calculatedValue = calculateTertiaryCase('Width');
                              formik.setFieldValue('tertiaryPackagingDetails.tertiaryCaseWidth', safeToFixed(calculatedValue));
                              formik.setFieldValue('tertiaryPackagingDetails.isTerCaseWidthAutoCalculated', true);
                              setHasUnsavedChanges(true);
                            }}
                            className="p-1 hover:bg-gray-100 rounded-full"
                          >
                            <RotateCcw className="h-3 w-3 text-gray-500" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Reset to auto-calculated value</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {renderUnitSelect("tertiaryCaseWidth", ["inch", "mm"])}
                </div>
              </div>
              <div className="label-bg w-1/3 flex items-center">
                <Label className="label lg:text-sm text-[9px] font-bold flex-grow flex items-center">
                  Tertiary Case Height (OD)
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help ml-1" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Formula: {secondaryPkg === 'true' ? 'Secondary Case Height(in) + Tertiary Package factor' : 'Package Height(in) + Tertiary package factor'}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <div className="flex items-center gap-1">
                  {!formik.values.tertiaryPackagingDetails.isTerCaseHeightAutoCalculated && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            title="Reset to auto-calculated value"
                            onClick={() => {
                              const calculatedValue = calculateTertiaryCase('Height');
                              formik.setFieldValue('tertiaryPackagingDetails.tertiaryCaseHeight', safeToFixed(calculatedValue));
                              formik.setFieldValue('tertiaryPackagingDetails.isTerCaseHeightAutoCalculated', true);
                              setHasUnsavedChanges(true);
                            }}
                            className="p-1 hover:bg-gray-100 rounded-full"
                          >
                            <RotateCcw className="h-3 w-3 text-gray-500" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Reset to auto-calculated value</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {renderUnitSelect("tertiaryCaseHeight", ["inch", "mm"])}
                </div>
              </div>
              { <div className="label-bg w-1/3 flex items-center">
                <Label className="label lg:text-sm text-[9px] font-bold flex-grow flex items-center">
                  Gauge/Thickness
                  
                </Label>
                
              </div>}
            </div>

            <div className="flex border-border-style-1 border w-full">
              <div className="relative w-1/3">
                <Input
                  name="tertiaryPackagingDetails.tertiaryCaseWidth"
                  placeholder="Enter"
                  type="number"
                  value={formik.values.tertiaryPackagingDetails.tertiaryCaseWidth}
                  onChange={(e) => {
                    formik.handleChange(e);
                    formik.setFieldValue('tertiaryPackagingDetails.isTerCaseWidthAutoCalculated', false);
                  }}
                  onBlur={formik.handleBlur}
                  className={cn(
                    formik.touched.tertiaryPackagingDetails?.tertiaryCaseWidth &&
                    formik.errors.tertiaryPackagingDetails?.tertiaryCaseWidth &&
                    "border-2 last:border-l-0 border-states-error outline-none",
                    formik.values.tertiaryPackagingDetails.isTerCaseWidthAutoCalculated && "bg-bgYellow"
                  )}
                />
                {formik.touched.tertiaryPackagingDetails?.tertiaryCaseWidth &&
                  formik.errors.tertiaryPackagingDetails?.tertiaryCaseWidth && (
                    <AlertTooltip
                      message={formik.errors.tertiaryPackagingDetails.tertiaryCaseWidth as string}
                    />
                  )}
              </div>

              <div className="relative w-1/3">
                <Input
                  name="tertiaryPackagingDetails.tertiaryCaseHeight"
                  placeholder="Enter"
                  type="number"
                  value={formik.values.tertiaryPackagingDetails.tertiaryCaseHeight}
                  onChange={(e) => {
                    formik.handleChange(e);
                    formik.setFieldValue('tertiaryPackagingDetails.isTerCaseHeightAutoCalculated', false);
                  }}
                  onBlur={formik.handleBlur}
                  className={cn(
                    formik.touched.tertiaryPackagingDetails?.tertiaryCaseHeight &&
                    formik.errors.tertiaryPackagingDetails?.tertiaryCaseHeight &&
                    "border-2 last:border-l-0 border-states-error outline-none",
                    formik.values.tertiaryPackagingDetails.isTerCaseHeightAutoCalculated && "bg-bgYellow"
                  )}
                />
                {formik.touched.tertiaryPackagingDetails?.tertiaryCaseHeight &&
                  formik.errors.tertiaryPackagingDetails?.tertiaryCaseHeight && (
                    <AlertTooltip
                      message={formik.errors.tertiaryPackagingDetails.tertiaryCaseHeight as string}
                    />
                  )}
              </div>
           {    <div className="relative w-1/3">
                <Input
                  name="tertiaryPackagingDetails.gauge"
                  placeholder="Enter"
                  type="number"
                  value={formik.values.tertiaryPackagingDetails.gauge}
                  onChange={(e) => {
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  className={cn(
                    formik.touched.tertiaryPackagingDetails?.gauge &&
                    formik.errors.tertiaryPackagingDetails?.gauge &&
                    "border-2 last:border-l-0 border-states-error outline-none",
                  )}
                />
                {formik.touched.tertiaryPackagingDetails?.gauge &&
                  formik.errors.tertiaryPackagingDetails?.gauge && (
                    <AlertTooltip
                      message={formik.errors.tertiaryPackagingDetails.gauge as string}
                    />
                  )}
              </div>}
            </div>
          </>
        )}
      </div>

      <Dialog open={isTertiaryImageOpen} onOpenChange={setIsTertiaryImageOpen}>
        <DialogContent className="max-w-[90vw] min-w-[50vw]  max-h-[90vh] w-auto">
          <img src={"/pallet-config/tertiary-pallet.jpg"} alt="Tertiary Package" className="w-full h-full object-contain" />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SecondTertiaryPakagingDetails;
